import { isEqual } from "lodash";

import Sentry from "@similarweb/sentry-logger";

import { createActivationApi } from "app/pages/activation";
import { METRICS_BY_CONTROLLER } from "app/pages/benchmark-tracker/components/Report/Tab/metricsByController";
import { BenchmarkControllers, BenchmarkTabs } from "app/pages/benchmark-tracker/config";
import { getIsMetricsFetched } from "app/pages/benchmark-tracker/helpers/apiHelpers";
import { getDateBySelectedGranularity } from "app/pages/benchmark-tracker/helpers/dateHelpers";
import type { BenchmarkType } from "app/pages/benchmark-tracker/hooks/types";
import { BenchmarkPrimaryEntityType } from "app/pages/benchmark-tracker/hooks/types";
import { createBenchmarkTrackerApi } from "app/pages/benchmark-tracker/services/api";
import { BenchmarkGranularity } from "app/pages/benchmark-tracker/services/api/constants";
import { BenchmarkMetricName } from "app/pages/benchmark-tracker/types/api.types";
import DurationService from "app/services/DurationService";
import { fetchWebsitesFaviconsQuery } from "app/services/sitesService/queries/sitesQueries";
import type { ThunkDispatchCommon, ThunkGetState } from "single-spa/store/types";

import { getBenchmark } from "../actions/entity";
import * as ac from "../actions/report";
import { addMetricData } from "../actions/report";
import { selectIsAssetsImagesFetched } from "../selectors/reportSelectors";

const getMetricsEffect =
    (
        tabName: BenchmarkControllers,
        metricName: BenchmarkMetricName,
        benchmarkId: string,
        country: number,
        startDate: string,
        granularity: BenchmarkGranularity,
    ) =>
    async (dispatch: ThunkDispatchCommon) => {
        const api = createBenchmarkTrackerApi();

        const isAudienceReach = metricName === BenchmarkMetricName.audienceReach;

        let startDateByMetric = startDate;
        let granularityByMetric = granularity;
        let requestTabName = tabName;

        if (isAudienceReach) {
            granularityByMetric = BenchmarkGranularity.MONTHLY;
            startDateByMetric = getDateBySelectedGranularity(startDate, granularity);
            dispatch(ac.setAudienceReachAutoDuration(startDateByMetric));
        }
        if (requestTabName === BenchmarkControllers.overview) {
            requestTabName = BenchmarkControllers.engagement;
        }

        return api.metric.fetchMetric(
            metricName,
            requestTabName,
            benchmarkId,
            country,
            startDateByMetric,
            granularityByMetric,
        );
    };

export const getTabMetricsEffect =
    (
        tabName: BenchmarkControllers,
        benchmarkId: string,
        country: number,
        duration: string,
        granularity: BenchmarkGranularity,
        metricsToRequestList: BenchmarkMetricName[],
    ) =>
    async (dispatch: ThunkDispatchCommon) => {
        const { from: startDate } = DurationService.getDurationData(duration).forAPI;
        const _startDate = startDate.replaceAll("|", "-");

        return Promise.all(
            metricsToRequestList.map(async (metricName) => {
                try {
                    const res = await dispatch(
                        getMetricsEffect(tabName, metricName, benchmarkId, country, _startDate, granularity),
                    );

                    dispatch(
                        addMetricData({ [metricName]: { ...res, isLoading: false, isError: false, isFetched: true } }),
                    );
                } catch (error) {
                    dispatch(addMetricData({ [metricName]: { isLoading: false, isError: true, isFetched: false } }));

                    throw error;
                }
            }),
        );
    };

export const getCurrentTabMetricsEffect =
    (benchmarkId: string, country: number, duration: string, granularity: BenchmarkGranularity, tab: number) =>
    async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        const report = getState().benchmarkTracker?.report;

        let forceFetch = false;

        if (!report.metrics) {
            forceFetch = true;
        }

        if (getIsMetricsFetched(report.metrics) && !forceFetch) {
            return;
        }

        try {
            dispatch(ac.getMetrics.request());

            const tabName = BenchmarkTabs[tab] as BenchmarkControllers;

            const metricsToRequestList = METRICS_BY_CONTROLLER[tabName];

            await dispatch(
                getTabMetricsEffect(tabName, benchmarkId, country, duration, granularity, metricsToRequestList),
            );

            dispatch(ac.getMetrics.success());
        } catch (error) {
            dispatch(ac.getMetrics.failure(error));
        }
    };

const getImageAssetsEffect = (domains: string[]) => async (dispatch: ThunkDispatchCommon) => {
    try {
        dispatch(ac.getMetricItemImage.request());

        const domainIcons = await fetchWebsitesFaviconsQuery(domains);

        dispatch(ac.getMetricItemImage.success(domainIcons));
    } catch (error) {
        dispatch(ac.getMetricItemImage.failure(error));
    }
};

export const getBenchmarkEffect =
    (benchmarkId: string) => async (dispatch: ThunkDispatchCommon, getState: ThunkGetState) => {
        dispatch(getBenchmark.request());
        const state = getState();

        const isImagesFetched = selectIsAssetsImagesFetched(state);

        try {
            const api = createBenchmarkTrackerApi();
            const activationApi = createActivationApi();

            const benchmark = await api.benchmark.getBenchmarkById(benchmarkId);
            if (!benchmark) {
                dispatch(getBenchmark.failure());
                return;
            }
            const benchmarkPrimaryDomain = benchmark.primaryDomains[0] ?? "";
            const images = await fetchWebsitesFaviconsQuery([benchmarkPrimaryDomain ?? ""]);
            const benchmarkWithImage = {
                ...benchmark,
                image: images[benchmarkPrimaryDomain],
                type: BenchmarkPrimaryEntityType.WEBSITE,
            };

            if (benchmarkId && benchmarkWithImage) {
                const selectedBenchmarkByParams: BenchmarkType = benchmarkWithImage;

                const activationSet = await activationApi.activation.getActivationById(
                    selectedBenchmarkByParams.activationSetId,
                );

                if (selectedBenchmarkByParams && activationSet) {
                    dispatch(getBenchmark.success({ ...selectedBenchmarkByParams, activationSet }));

                    const domains = [activationSet.primaryDomain, ...activationSet.competitorsDomains];

                    if (isImagesFetched) {
                        const fetchedDomains = Object.keys(state.benchmarkTracker.report.assets.images);

                        if (!isEqual(fetchedDomains, domains)) {
                            await dispatch(getImageAssetsEffect(domains));
                        }
                    } else {
                        await dispatch(getImageAssetsEffect(domains));
                    }
                }
            }
        } catch (error) {
            dispatch(getBenchmark.failure());
            Sentry.error("Benchmark or activationSet get error", error);
        }
    };

export const getGAMetaDataEffect = (benchmarkId: string) => async (dispatch: ThunkDispatchCommon) => {
    try {
        dispatch(ac.getMetaData.request());
        const api = createBenchmarkTrackerApi();

        const result = await api.report.fetchGAMetaData(benchmarkId);
        dispatch(ac.getMetaData.success(result));

        return result;
    } catch (error) {
        Sentry.errorTracker({
            message: "BenchmarkTracker: Error when fetching GA meta data",
            e: error,
            level: "error",
            params: {
                extra: {
                    benchmarkId,
                },
            },
        });

        dispatch(ac.getMetaData.failure(error));
    }
};

export const getCompetitorsEffect =
    (
        metricName: BenchmarkMetricName,
        benchmarkId: string,
        country: number,
        duration: string,
        granularity: BenchmarkGranularity,
    ) =>
    async (dispatch: ThunkDispatchCommon) => {
        const api = createBenchmarkTrackerApi();

        const isAudienceReach = metricName === BenchmarkMetricName.audienceReach;

        const { from: startDate } = DurationService.getDurationData(duration).forAPI;
        const _startDate = startDate.replaceAll("|", "-");

        let startDateByMetric = _startDate;
        let granularityByMetric = granularity;

        if (isAudienceReach) {
            granularityByMetric = BenchmarkGranularity.MONTHLY;
            startDateByMetric = getDateBySelectedGranularity(_startDate, granularity);
        }

        try {
            dispatch(ac.getCompetitors.request());
            const competitors = await api.metric.fetchMetric(
                metricName,
                BenchmarkControllers.engagement,
                benchmarkId,
                country,
                startDateByMetric,
                granularityByMetric,
                true,
            );

            dispatch(ac.getCompetitors.success({ competitors, metricName }));
        } catch (error) {
            dispatch(ac.getCompetitors.failure(error));
            Sentry.errorTracker({
                message: "BenchmarkTracker: Error when fetching competitors",
                e: error,
                level: "error",
                params: {
                    extra: {
                        benchmarkId,
                        country,
                        duration,
                        granularity,
                        metricName,
                    },
                },
            });
        }
    };
